import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Loader2, MailCheck } from 'lucide-react';
import { BrandLogo } from '../../components/common/BrandLogo';
import { forgotPassword } from '../../services/api/auth';
import { formatApiErrorDetail } from '../../services/api/client';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err: any) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050914] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute w-[800px] h-[800px] bg-cyan-500/10 blur-[120px] rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="w-full max-w-md bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-3xl p-8 sm:p-12 relative z-10 shadow-2xl">
        <div className="mb-8">
          <BrandLogo />
        </div>

        {sent ? (
          <div data-testid="forgot-sent" className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center mx-auto mb-5">
              <MailCheck className="w-7 h-7 text-emerald-400" strokeWidth={1.5} />
            </div>
            <h1 className="text-xl font-medium text-white mb-2">Periksa email Anda</h1>
            <p className="text-white/60 text-sm mb-6">Jika email terdaftar, kami telah mengirim tautan untuk mereset password. Tautan berlaku 1 jam.</p>
            <Link to="/" className="text-cyan-400 hover:text-cyan-300 text-sm">Kembali ke Login</Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl tracking-tight font-light text-white mb-2">Lupa password?</h1>
            <p className="text-white/50 text-sm mb-8">Masukkan email Anda, kami kirimkan tautan reset.</p>
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div>
                <label className="block text-sm text-white/70 mb-2">Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" placeholder="you@email.com"
                  className="w-full bg-[#090D1A] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors" />
              </div>
              {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5" data-testid="forgot-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="forgot-submit"
                className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-3 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-60 flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Kirim Tautan Reset
              </button>
            </form>
            <p className="text-sm text-white/50 mt-8 text-center">
              Ingat password Anda? <Link to="/" className="text-cyan-400 hover:text-cyan-300">Masuk</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
